// Utilitários para navegação simples e roteamento baseado em Slugs de URL

export interface NavigationState {
  section: string;
  subSection?: string;
  params?: Record<string, any>;
}

// Estados padrão para cada seção
export const DEFAULT_SECTIONS: Record<string, NavigationState> = {
  home: { section: 'home' },
  'my-products': { section: 'my-products' },
  mentorias: { section: 'mentorias' },
  'mentorias-2': { section: 'mentorias-2' },
  'useful-links': { section: 'useful-links' },
  suppliers: { section: 'suppliers' },
  calendar: { section: 'calendar' },
  community: { section: 'community', subSection: 'categories' },
  support: { section: 'support' },
  competicao: { section: 'competicao' },
  player: { section: 'player' },
  admin: { section: 'admin', subSection: 'overview' },
  profile: { section: 'profile' },
  integrations: { section: 'integrations' },
  'bio-links': { section: 'bio-links' },
  'mentoria-hub': { section: 'mentoria-hub' },
};

// Mapeamento de seção -> Slug de URL
export const SECTION_TO_SLUG: Record<string, string> = {
  home: '/',
  'my-products': '/compras',
  mentorias: '/mentorias',
  'mentorias-2': '/mentores-2',
  'useful-links': '/ferramentas',
  suppliers: '/fornecedores',
  calendar: '/calendario',
  community: '/comunidade',
  support: '/suporte',
  competicao: '/competicao',
  admin: '/admin',
  profile: '/perfil',
  integrations: '/integracoes',
  player: '/player',
};

// Obter a seção ativa baseada no pathname e query string do navegador
export const getSectionFromPathname = (pathname: string, search: string = ''): { section: string; subSection?: string } => {
  const cleanPath = pathname.toLowerCase();
  const searchParams = new URLSearchParams(search);
  const urlAula = searchParams.get('aula') || searchParams.get('c');
  const bioParam = searchParams.get('bio');

  // Bio Link do Mentor
  if (
    cleanPath.startsWith('/bio-') ||
    cleanPath.startsWith('/links-') ||
    cleanPath.startsWith('/bio/') ||
    cleanPath.startsWith('/links/') ||
    bioParam
  ) {
    return { section: 'bio-links' };
  }

  // Hub da Mentoria estilo Hubla
  if (cleanPath.startsWith('/mentoria-') || cleanPath.startsWith('/mentoria/')) {
    return { section: 'mentoria-hub' };
  }

  // Player de Vídeo
  if (urlAula || cleanPath.startsWith('/player') || cleanPath.startsWith('/aula')) {
    const urlVideo = searchParams.get('video') || searchParams.get('v');
    const subSection = urlAula ? (urlVideo ? `player:${urlAula}:${urlVideo}` : `player:${urlAula}`) : undefined;
    return { section: 'player', subSection };
  }

  // Mapeamentos diretos de slugs de URL
  if (cleanPath === '/compras' || cleanPath === '/meus-produtos') return { section: 'my-products' };
  if (cleanPath === '/mentorias') return { section: 'mentorias' };
  if (cleanPath === '/mentores-2') return { section: 'mentorias-2' };
  if (cleanPath === '/ferramentas' || cleanPath === '/links' || cleanPath === '/useful-links') return { section: 'useful-links' };
  if (cleanPath === '/fornecedores' || cleanPath === '/suppliers') return { section: 'suppliers' };
  if (cleanPath === '/calendario' || cleanPath === '/calendar') return { section: 'calendar' };
  if (cleanPath === '/comunidade' || cleanPath === '/community') return { section: 'community' };
  if (cleanPath === '/suporte' || cleanPath === '/support') return { section: 'support' };
  if (cleanPath === '/competicao' || cleanPath === '/desafio' || cleanPath === '/challenge' || cleanPath === '/ranking-entregas' || cleanPath === '/desafio-black-privado-2026') return { section: 'competicao' };
  if (cleanPath === '/admin') return { section: 'admin' };
  if (cleanPath === '/perfil' || cleanPath === '/profile') return { section: 'profile' };
  if (cleanPath === '/integracoes' || cleanPath === '/integrations') return { section: 'integrations' };
  if (cleanPath === '/' || cleanPath === '' || cleanPath === '/home') return { section: 'home' };

  return { section: 'home' };
};

// Obter a URL de pathname para cada seção
export const getPathnameFromSection = (section: string): string => {
  return SECTION_TO_SLUG[section] || '/';
};

// Função para verificar se o usuário está na página inicial de uma seção
export const isAtSectionHome = (currentSection: string, currentSubSection?: string): boolean => {
  const defaultState = DEFAULT_SECTIONS[currentSection];
  if (!defaultState) return true;
  return !currentSubSection || currentSubSection === defaultState.subSection;
};

// Função para navegar para a página inicial de uma seção
export const navigateToSectionHome = (
  section: string,
  onSectionChange: (section: string) => void,
  onSubSectionChange?: (subSection: string) => void
): void => {
  const defaultState = DEFAULT_SECTIONS[section];
  if (defaultState) {
    onSectionChange(section);
    if (defaultState.subSection && onSubSectionChange) {
      onSubSectionChange(defaultState.subSection);
    }
  }
};

// Função para verificar se deve redirecionar para a página inicial
export const shouldRedirectToHome = (
  currentSection: string,
  targetSection: string,
  currentSubSection?: string
): boolean => {
  if (currentSection === targetSection) {
    return !isAtSectionHome(currentSection, currentSubSection);
  }
  return false;
};